import type { ReactNode } from 'react';
import { Spinner } from '@/components/ui/Spinner';

interface QueryStateProps {
  isLoading: boolean;
  error: unknown;
  isEmpty?: boolean;
  emptyMessage?: string;
  loadingLabel?: string;
  /** Optional skeleton shown while loading instead of the default spinner. */
  skeleton?: ReactNode;
  children: ReactNode;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Something went wrong while loading data.';
}

/** Small helper to render the common loading / error / empty states. */
export function QueryState({
  isLoading,
  error,
  isEmpty,
  emptyMessage = 'Nothing here yet.',
  loadingLabel = 'Loading',
  skeleton,
  children,
}: QueryStateProps) {
  if (isLoading) {
    if (skeleton) {
      return (
        <div aria-busy="true">
          {skeleton}
          <span role="status" className="sr-only">
            {loadingLabel}
          </span>
        </div>
      );
    }
    return (
      <div className="py-8">
        <Spinner label={loadingLabel} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
      >
        {errorMessage(error)}
      </div>
    );
  }

  if (isEmpty) {
    return <p className="py-8 text-sm text-gray-500">{emptyMessage}</p>;
  }

  return <>{children}</>;
}
